import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useAngemeldet } from "../../kern/sitzung";
import { useAnsicht } from "../../kern/ansichten";
import { anzeigename } from "../../kern/namen";
import { usePersonen } from "../../kern/personen";
import { heute } from "../../kern/zeit";
import { fehlertext } from "../../kern/fehler";
import { Karte } from "../../kern/ui/Karte";
import { Knopf } from "../../kern/ui/Knopf";
import { Kalender } from "../../kern/ui/Kalender";
import { Hilfe } from "../../kern/ui/Hilfe";
import { Schalter } from "../../kern/ui/Schalter";
import { Wertwahl, type WertOption } from "../../kern/ui/Wertwahl";
import { NeueAufgabeKnopf } from "../../kern/ui/NeueAufgabeKnopf";
import { Leer, Meldung } from "../../kern/ui/Leer";
import { Reiter, ReiterInhalt } from "../../kern/ui/Reiter";
import { Segment } from "../../kern/ui/Segment";
import { useHinweis } from "../../kern/ui/Hinweise";
import seiten from "../../kern/seiten/seiten.module.css";
import { useAufgaben, useRessortFarben, useRessorts } from "./daten";
import { mitPunkt, type Aufgabe, type Status } from "./logik";
import { useSchnellaktionen } from "./aktionen";
import { Rueckfrage } from "./Rueckfrage";
import { GRUPPIERUNG, gruppieren, ohnePerson, restText, sortierenImSprint, sprintFortschritt, zeitraum, type Gruppierung, type Sprint as SprintTyp, type SprintFortschritt } from "./sprintlogik";
import { tagMitWochentag } from "../../kern/kalender";
import { AufgabenListe, Geloescht, geloeschteIn, STATUS_ZEILE, StatusGruppen, type NeueZeile } from "./Liste";
import { personGruppe } from "./vorgaben";
import { titelHoeheSetzen } from "./titelhoehe";
import { useLoeschen } from "./loeschen";
import { laufenderSprint, useSprintAktionen, useSprintErgebnisse, useSprints } from "./sprints";
import { SprintAbschluss } from "./SprintAbschluss";
import { SprintBoard } from "./SprintBoard";
import s from "./sprint.module.css";
import { Laedt } from "../../kern/ui/Laedt";

type Bereich = "backlog" | "sprint" | "archiv";

/** Erklärung der Sprint-Seite, seit BAUPLAN-SPRINT-KOPF.md hinter „?“ neben dem Titel (gilt für alle drei Reiter) */
const ERKLAERUNG =
  "Der Backlog sammelt, was ansteht. Im Sprint steht, was ihr euch für zwei Wochen vornehmt, mit Unteraufgaben, Personen, Priorität und Ressort. Beim Abschluss geht Erledigtes mit dem Sprint ins Archiv, Offenes in den nächsten Sprint oder zurück in den Backlog.";

const GRUPPIERUNG_OPTIONEN: readonly WertOption<Gruppierung>[] = (Object.keys(GRUPPIERUNG) as Gruppierung[]).map((g) => ({ wert: g, text: GRUPPIERUNG[g] }));

/** Balken nach Status (BAUPLAN-SPRINT-KOPF.md, Abschnitt 5): Reihenfolge Erledigt, In Arbeit, Wartet, Zu tun. Die Farben sind die
 *  kräftigeren Stufen der Töne aus der Liste (STATUS_ZEILE), aus tokens.css: die Flächen der Pillen wären als Balken zu hell. */
const BALKEN_REIHE: readonly Status[] = ["erledigt", "in_arbeit", "wartet", "zu_tun"];
const BALKEN_FARBE: Record<string, string> = { ok: "var(--ok)", warten: "var(--warn)", wartet: "var(--wartet-ink)", grau: "var(--gray-ink)", neutral: "var(--dashed)" };
const balkenFarbe = (st: Status) => BALKEN_FARBE[STATUS_ZEILE.find((o) => o.wert === st)?.ton ?? "neutral"] ?? "var(--dashed)";
const statusText = (st: Status) => STATUS_ZEILE.find((o) => o.wert === st)?.text ?? st;

export const SprintSeite = () => <Sprint bereich="sprint" />;
export const BacklogSeite = () => <Sprint bereich="backlog" />;
export const ArchivSeite = () => <Sprint bereich="archiv" />;

/** Ansicht Sprint (Umbau 12.10.2026, ADR-011): Backlog, laufender Sprint, Archiv. Nur mit sprint.planen. */
function Sprint({ bereich }: { bereich: Bereich }) {
  const sprints = useSprints();
  // Mit gelöschten, für „Gelöscht (n)“ unter Sprint und Backlog
  const aufgaben = useAufgaben(true);
  const personen = usePersonen();
  const ressorts = useRessorts();
  const farben = useRessortFarben();
  const sa = useSchnellaktionen();
  const loeschen = useLoeschen(sa);

  const laufend = laufenderSprint(sprints.data);
  const mitGeloeschten = aufgaben.data ?? [];
  const alle = mitGeloeschten.filter((a) => !a.archiviert_am);
  const haupt = alle.filter((a) => !a.eltern_id);
  const imSprint = laufend ? haupt.filter((a) => a.sprint_id === laufend.id) : [];
  const imBacklog = haupt.filter((a) => !a.sprint_id);
  const archiv = (sprints.data ?? []).filter((x) => x.status === "abgeschlossen");
  const unter = new Map<string, Aufgabe[]>();
  for (const a of alle) if (a.eltern_id) unter.set(a.eltern_id, [...(unter.get(a.eltern_id) ?? []), a]);
  const unterVon = (id: string) => unter.get(id) ?? [];
  const gemeinsam = { personen: personen.data, ressorts: ressorts.data, farben: farben.data, sa, loeschen };
  const geloeschtImSprint = laufend ? geloeschteIn(mitGeloeschten, (a) => a.sprint_id === laufend.id) : [];
  const geloeschtImBacklog = geloeschteIn(mitGeloeschten, (a) => !a.sprint_id);

  const reiter: { id: Bereich; pfad: string; label: string; zahl: number }[] = [
    { id: "backlog", pfad: "/sprint/backlog", label: "Backlog", zahl: imBacklog.length },
    { id: "sprint", pfad: "/sprint", label: laufend?.name ?? "Sprint", zahl: imSprint.length },
    { id: "archiv", pfad: "/sprint/archiv", label: "Archiv", zahl: archiv.length },
  ];

  return (
    <div className={seiten.stapel}>
      <div className={seiten.kopf}>
        {/* Erklärung hinter „?“ statt dauerhaft unter den Reitern (BAUPLAN-SPRINT-KOPF.md, Abschnitt 1) */}
        <div className={s.titelZeile}>
          <h1>Sprint.</h1>
          <Hilfe label="Erklärung zum Sprint">
            <p>{ERKLAERUNG}</p>
          </Hilfe>
        </div>
        <NeueAufgabeKnopf vorgabe={{ wo: bereich === "backlog" ? "backlog" : "sprint" }} />
      </div>
      <Reiter label="Backlog, Sprint und Archiv" eintraege={reiter} aktiv={bereich} panel="sprint-inhalt" />
      <ReiterInhalt panel="sprint-inhalt" aktiv={bereich} className={seiten.stapel}>
        {aufgaben.isError ? <Meldung fehler>{fehlertext(aufgaben.error)}</Meldung> : null}
        {sprints.isError ? <Meldung fehler>{fehlertext(sprints.error)}</Meldung> : null}
        {aufgaben.isPending || sprints.isPending ? <Laedt /> : null}
        {aufgaben.data && sprints.data ? (
          bereich === "sprint" ? (
            laufend ? (
              <LaufenderSprint sprint={laufend} liste={imSprint} unterVon={unterVon} geloescht={geloeschtImSprint} {...gemeinsam} />
            ) : (
              <Leer titel="Gerade läuft kein Sprint." />
            )
          ) : bereich === "backlog" ? (
            <Backlog liste={imBacklog} laufend={laufend} unterVon={unterVon} geloescht={geloeschtImBacklog} {...gemeinsam} />
          ) : (
            <Archiv sprints={archiv} alle={sprints.data} laufend={laufend} aufgaben={alle} unterVon={unterVon} {...gemeinsam} />
          )
        ) : null}
      </ReiterInhalt>
      <Rueckfrage text={sa.rueckfrage?.text ?? null} onAntwort={(mit) => sa.rueckfrageAntwort(mit)} onAbbrechen={sa.rueckfrageSchliessen} />
      {loeschen.rueckfrage}
    </div>
  );
}

interface Gemeinsam {
  personen: ReturnType<typeof usePersonen>["data"];
  ressorts: ReturnType<typeof useRessorts>["data"];
  farben: ReturnType<typeof useRessortFarben>["data"];
  sa: ReturnType<typeof useSchnellaktionen>;
  loeschen: ReturnType<typeof useLoeschen>;
}

function LaufenderSprint({
  sprint,
  liste,
  unterVon,
  geloescht,
  ...gemeinsam
}: { sprint: SprintTyp; liste: Aufgabe[]; unterVon: (id: string) => readonly Aufgabe[]; geloescht: Aufgabe[] } & Gemeinsam) {
  const { personen, ressorts, sa } = gemeinsam;
  const { person: ich, rechte } = useAngemeldet();
  const { zeigen } = useHinweis();
  const { bearbeiten } = useSprintAktionen();
  const [ansicht, setAnsicht] = useState<"liste" | "board">("liste");
  // Vorgabe „nach Status“, gemerkt je Person auf allen Geräten (BAUPLAN-STATUS-IN-LISTEN.md, Abschnitt 2 und 4)
  const [gruppe, setGruppe] = useAnsicht<Gruppierung>("sprint.gruppierung", Object.keys(GRUPPIERUNG) as Gruppierung[], "status");
  const [nurMeine, setNurMeine] = useState(false);
  const [abschluss, setAbschluss] = useState(false);
  const f = sprintFortschritt(liste);
  const tag = heute();
  const ueberfaellig = liste.filter((a) => a.ueberfaellig).length + liste.flatMap((a) => unterVon(a.id)).filter((u) => u.ueberfaellig).length;
  const ohne = ohnePerson(liste);
  const sichtbar = nurMeine ? liste.filter((a) => a.personen.includes(ich.id) || unterVon(a.id).some((u) => u.personen.includes(ich.id))) : liste;
  const namen = {
    personen: (personen ?? []).filter((p) => p.aktiv).map((p) => ({ id: p.id, name: anzeigename(p) })),
    ressorts: ressorts ?? [],
  };
  const fehler = (e: unknown) => zeigen(fehlertext(e), { fehler: true });

  /** Zeile „+ Aufgabe hinzufügen“ je Gruppe (BAUPLAN-AUFGABENLISTE.md, Abschnitt 5): im Sprint, dazu Person oder Ressort der Gruppe. Nach
   *  Status setzt StatusGruppen den Status der Gruppe. Mit „Nur meine“ nur, wo die neue Aufgabe bei mir liegt. */
  function neuFuer(schluessel: string): NeueZeile | undefined {
    if (gruppe === "keine" || gruppe === "status") return { vorgabe: { wo: "sprint" } };
    if (gruppe === "ressort") return { vorgabe: { wo: "sprint", ressort: schluessel || null } };
    if (nurMeine && schluessel !== ich.id) return undefined;
    const p = personGruppe(schluessel, { ich: ich.id, vorstand: rechte.has("aufgaben.alle_bearbeiten"), personIstVorstand: !!personen?.find((x) => x.id === schluessel)?.vorstand });
    return p ? { vorgabe: { ...p, wo: "sprint" } } : undefined;
  }

  function zielSpeichern(ziel: string, zurueck: () => void) {
    bearbeiten.mutate(
      { sprint: sprint.id, ziel },
      {
        onSuccess: () => zeigen("Sprintziel gespeichert."),
        onError: (e) => {
          zurueck();
          fehler(e);
        },
      },
    );
  }

  return (
    <>
      <Karte>
        {/* Kopf nach dem Designsystem (BAUPLAN-SPRINT-KOPF.md): Zeitraum einmal mit dem Ende als Kalender, Ziel lesen und bearbeiten,
            Fortschritt als Balken nach Status, „Sprint abschließen“ als normaler Knopf */}
        <div className={s.kopf}>
          <div className={s.kopfLinks}>
            <h2 className={s.name}>{sprint.name}</h2>
            {/* Der Trennpunkt hängt am Teil davor, damit eine neue Zeile nie mit „·“ beginnt. Leerzeichen zwischen den Teilen nur für
                Screenreader und Kopieren, den Abstand macht gap. */}
            <div className={s.zeitraum}>
              <span className={s.teil}>{tagMitWochentag(sprint.beginn)} bis</span>{" "}
              <span className={[s.teil, s.teilKnopf].join(" ")}>
                <Kalender
                  label="Ende des Sprints"
                  knopfLabel={`Ende des Sprints: ${tagMitWochentag(sprint.ende)}, ändern`}
                  knopfKlasse={s.endeKnopf}
                  anzeige={tagMitWochentag(sprint.ende)}
                  wert={sprint.ende}
                  min={sprint.beginn}
                  sprint={{ beginn: sprint.beginn, ende: sprint.ende, name: sprint.name }}
                  onWahl={(tag) =>
                    tag &&
                    tag !== sprint.ende &&
                    bearbeiten.mutate({ sprint: sprint.id, ende: tag }, { onSuccess: () => zeigen("Ende geändert. Der Sprint heißt jetzt nach dem neuen Ende."), onError: fehler })
                  }
                />{" "}
                ·
              </span>{" "}
              <span className={s.teil}>
                {restText(sprint, tag)}
                {ueberfaellig || ohne ? " ·" : null}
              </span>
              {ueberfaellig ? (
                <>
                  {" "}
                  <span className={s.teil}>
                    <span className={s.ueber}>{ueberfaellig} überfällig</span>
                    {ohne ? " ·" : null}
                  </span>
                </>
              ) : null}
              {ohne ? (
                <>
                  {" "}
                  <span className={s.teil}>
                    <Link to="/offen?wo=sprint" className={s.ohneLink}>
                      {ohne} ohne Person
                    </Link>
                  </span>
                </>
              ) : null}
            </div>
            <Sprintziel ziel={sprint.ziel} onSpeichern={zielSpeichern} />
          </div>
          <div className={s.kopfRechts}>
            <p className={s.prozent}>{f.prozent} %</p>
            <Knopf onClick={() => setAbschluss(true)}>Sprint abschließen</Knopf>
          </div>
          <StatusBalken f={f} />
        </div>
      </Karte>
      <div className={s.werkzeug}>
        <Segment
          legende="Ansicht"
          optionen={[
            { wert: "liste", label: "Liste" },
            { wert: "board", label: "Board" },
          ]}
          wert={ansicht}
          onWechsel={setAnsicht}
        />
        {ansicht === "liste" ? <Wertwahl art="Gruppieren" vorsatz="Gruppieren:" optionen={GRUPPIERUNG_OPTIONEN} wert={gruppe} klein onWechsel={setGruppe} /> : null}
        <Schalter label="Nur meine" checked={nurMeine} onChange={(e) => setNurMeine(e.target.checked)} />
      </div>
      {ansicht === "board" ? (
        <SprintBoard liste={sichtbar} unterVon={unterVon} personen={personen} ressorts={ressorts} sa={sa} />
      ) : sichtbar.length === 0 ? (
        <Karte>
          <Leer titel={nurMeine ? "Nichts von dir in diesem Sprint." : "Keine Aufgaben im Sprint."}>Leg mit „Neue Aufgabe“ eine an oder hol eine aus dem Backlog.</Leer>
        </Karte>
      ) : gruppe === "status" ? (
        <section aria-label={`Aufgaben in ${sprint.name}`} className={s.gruppen}>
          <StatusGruppen
            liste={sortierenImSprint(sichtbar)}
            unterVon={unterVon}
            label={`Aufgaben in ${sprint.name}`}
            sprint={{ beginn: sprint.beginn, ende: sprint.ende, name: sprint.name }}
            neu={neuFuer("")}
            {...gemeinsam}
          />
        </section>
      ) : (
        gruppieren(sichtbar, gruppe, namen).map((g) => (
          <section key={g.schluessel || "ohne"} aria-label={gruppe === "keine" ? `Aufgaben in ${sprint.name}` : g.titel}>
            {gruppe === "keine" ? null : (
              <h3 className={s.gruppeTitel}>
                {g.titel} <span>{g.liste.length}</span>
              </h3>
            )}
            <AufgabenListe
              liste={g.liste}
              unterVon={unterVon}
              label={gruppe === "keine" ? `Aufgaben in ${sprint.name}` : `${sprint.name}, ${g.titel}`}
              sprint={{ beginn: sprint.beginn, ende: sprint.ende, name: sprint.name }}
              neu={neuFuer(g.schluessel)}
              {...gemeinsam}
            />
          </section>
        ))
      )}
      <Geloescht liste={geloescht} personen={personen} loeschen={gemeinsam.loeschen} />
      <SprintAbschluss offen={abschluss} sprint={sprint} aufgaben={liste} personen={personen} onSchliessen={() => setAbschluss(false)} />
    </>
  );
}

/** Sprintziel (BAUPLAN-SPRINT-KOPF.md, Abschnitt 4): erst lesen, ganz und umbrochen, dann „Bearbeiten“. Im Feld speichert Enter oder Klick
 *  daneben wie bisher, Esc verwirft. Ohne Ziel grau „Noch kein Sprintziel.“ und „Ziel eintragen“. Nach dem Speichern steht gleich der neue
 *  Text da, bis der Sprint neu geladen ist. Scheitert das Speichern, ruft `onSpeichern` `zurueck` auf und das alte Ziel steht wieder da. */
export function Sprintziel({ ziel, onSpeichern }: { ziel: string; onSpeichern: (ziel: string, zurueck: () => void) => void }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(ziel);
  // Gespeicherter Text, solange `ziel` noch der alte ist (`basis`). Kommt das neue Ziel aus der Datenbank, gilt wieder `ziel`.
  const [vorlaeufig, setVorlaeufig] = useState<{ basis: string; text: string } | null>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  // Nach Esc kein Speichern beim Verlassen des Felds, danach Fokus zurück auf den Knopf
  const verworfen = useRef(false);
  const zurueck = useRef(false);

  // Das Feld wächst mit dem Ziel, so steht ein langes Ziel auch am Handy ganz da
  useLayoutEffect(() => {
    if (bearbeiten && feld.current) titelHoeheSetzen(feld.current);
  }, [bearbeiten, text]);

  useEffect(() => {
    if (bearbeiten) {
      feld.current?.focus();
      feld.current?.select();
    } else if (zurueck.current) {
      zurueck.current = false;
      knopf.current?.focus();
    }
  }, [bearbeiten]);

  const angezeigt = vorlaeufig?.basis === ziel ? vorlaeufig.text : ziel;

  function fertig() {
    const neu = text.trim();
    if (!verworfen.current && neu !== angezeigt) {
      setVorlaeufig({ basis: ziel, text: neu });
      onSpeichern(neu, () => setVorlaeufig(null));
    }
    verworfen.current = false;
    setBearbeiten(false);
  }

  if (bearbeiten) {
    return (
      <div className={s.ziel}>
        <label className={s.feldLabel} htmlFor="sprintziel">
          Sprintziel
        </label>
        <textarea
          ref={feld}
          id="sprintziel"
          className={[s.feld, s.zielFeld].join(" ")}
          value={text}
          maxLength={1000}
          rows={1}
          placeholder="Was soll am Ende stehen?"
          onChange={(e) => setText(e.target.value.replace(/\n/g, " "))}
          onBlur={fertig}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              zurueck.current = true;
              feld.current?.blur();
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              verworfen.current = true;
              zurueck.current = true;
              setText(angezeigt);
              feld.current?.blur();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className={s.ziel}>
      <span className={s.feldLabel}>Sprintziel</span>
      <div className={s.zielLesen}>
        {angezeigt ? <p className={s.zielText}>{angezeigt}</p> : <p className={[s.zielText, s.zielLeer].join(" ")}>Noch kein Sprintziel.</p>}
        <button
          ref={knopf}
          type="button"
          className={s.zielKnopf}
          aria-label={angezeigt ? "Bearbeiten: Sprintziel" : "Ziel eintragen"}
          onClick={() => {
            setText(angezeigt);
            setBearbeiten(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
          </svg>
          {angezeigt ? "Bearbeiten" : "Ziel eintragen"}
        </button>
      </div>
    </div>
  );
}

/** Fortschritt einmal (BAUPLAN-SPRINT-KOPF.md, Abschnitt 5): Balken nach Status mit Legende. Status nie nur als Farbe, Wort und Zahl
 *  stehen in der Legende, der Balken hat einen Vorlesetext. */
export function StatusBalken({ f }: { f: SprintFortschritt }) {
  const teile = BALKEN_REIHE.map((st) => ({ st, n: f.jeStatus[st] }));
  return (
    <div className={s.fortschritt}>
      <div className={s.statusBalken} role="img" aria-label={teile.map((t) => `${statusText(t.st)} ${t.n}`).join(", ")}>
        {teile
          .filter((t) => t.n > 0)
          .map((t) => (
            <span key={t.st} style={{ flexGrow: t.n, background: balkenFarbe(t.st) }} />
          ))}
      </div>
      <ul className={s.legende} aria-hidden="true">
        {teile.map((t) => (
          <li key={t.st}>
            <i style={{ background: balkenFarbe(t.st) }} />
            {statusText(t.st)} {t.n}
          </li>
        ))}
        <li>{f.gesamt === 1 ? "1 Aufgabe" : `${f.gesamt} Aufgaben`}</li>
      </ul>
    </div>
  );
}

function Backlog({
  liste,
  laufend,
  unterVon,
  geloescht,
  ...gemeinsam
}: { liste: Aufgabe[]; laufend: SprintTyp | undefined; unterVon: (id: string) => readonly Aufgabe[]; geloescht: Aufgabe[] } & Gemeinsam) {
  const { sa } = gemeinsam;
  const inSprint = (a: Aufgabe) => sa.inSprint(a, mitPunkt(`„${a.titel}“ ist jetzt in ${laufend?.name ?? "dem Sprint"}`), true);
  return (
    <>
      <p className={s.meta}>Planung: Holt Aufgaben mit „In den Sprint“ in {laufend?.name ?? "den laufenden Sprint"} Sortiert nach Priorität.</p>
      {liste.length === 0 ? (
        <Karte>
          <Leer titel="Der Backlog ist leer." />
        </Karte>
      ) : (
        <AufgabenListe
          liste={sortierenImSprint(liste)}
          unterVon={unterVon}
          label="Backlog"
          onInSprint={laufend ? inSprint : undefined}
          neu={{ vorgabe: { wo: "backlog" } }}
          {...gemeinsam}
        />
      )}
      <Geloescht liste={geloescht} personen={gemeinsam.personen} loeschen={gemeinsam.loeschen} />
    </>
  );
}

function Archiv({
  sprints,
  alle,
  laufend,
  aufgaben,
  unterVon,
  ...gemeinsam
}: { sprints: SprintTyp[]; alle: SprintTyp[]; laufend: SprintTyp | undefined; aufgaben: Aufgabe[]; unterVon: (id: string) => readonly Aufgabe[] } & Gemeinsam) {
  const ergebnisse = useSprintErgebnisse();
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const [abschluss, setAbschluss] = useState(false);
  if (sprints.length === 0) {
    // Ist der laufende Sprint schon abgelaufen, gleich hier abschließen (Bauplan 4.8)
    const abgelaufen = !!laufend && laufend.ende < heute();
    return (
      <Karte>
        <Leer titel="Noch kein Sprint abgeschlossen.">
          {laufend ? `Wenn ihr ${laufend.name} abschließt, steht er hier: mit Ziel, Zeitraum und allem, was erledigt wurde.` : null}
        </Leer>
        {laufend && abgelaufen ? (
          <>
            <Knopf art="primaer" onClick={() => setAbschluss(true)}>
              {laufend.name} jetzt abschließen
            </Knopf>
            <SprintAbschluss
              offen={abschluss}
              sprint={laufend}
              aufgaben={aufgaben.filter((a) => !a.eltern_id && a.sprint_id === laufend.id)}
              personen={gemeinsam.personen}
              onSchliessen={() => setAbschluss(false)}
            />
          </>
        ) : null}
      </Karte>
    );
  }
  return (
    <>
      {ergebnisse.isError ? <Meldung fehler>{fehlertext(ergebnisse.error)}</Meldung> : null}
      {sprints.map((sp) => {
        const e = (ergebnisse.data ?? []).filter((x) => x.sprint_id === sp.id);
        const erledigt = e.filter((x) => x.ergebnis === "erledigt");
        const erledigtImSprint = aufgaben.filter((a) => !a.eltern_id && a.sprint_id === sp.id).sort((a, b) => a.titel.localeCompare(b.titel, "de"));
        const weiter = e.filter((x) => x.ergebnis === "naechster_sprint").length;
        const zurueck = e.filter((x) => x.ergebnis === "backlog").length;
        const danach = alle.filter((x) => x.beginn > sp.ende).sort((a, b) => a.beginn.localeCompare(b.beginn))[0];
        const auf = !!offen[sp.id];
        return (
          <Karte key={sp.id}>
            <div className={s.archivKopf}>
              <div>
                <h2>{sp.name}</h2>
                <span className={s.meta}>{zeitraum(sp)}, abgeschlossen</span>
              </div>
              <div>
                <strong>
                  {erledigt.length} von {e.length}
                </strong>{" "}
                erledigt
              </div>
            </div>
            {sp.ziel ? (
              <p>
                <span className={s.feldLabel}>Ziel</span>
                <br />
                {sp.ziel}
              </p>
            ) : null}
            <p className={s.meta}>
              {weiter} offen in {danach?.name ?? "den nächsten Sprint"} übernommen · {zurueck} zurück in den Backlog
            </p>
            <Knopf klein aria-expanded={auf} onClick={() => setOffen({ ...offen, [sp.id]: !auf })}>
              {auf ? "Erledigte ausblenden" : "Erledigte anzeigen"}
            </Knopf>
            {auf ? (
              erledigtImSprint.length === 0 ? (
                <p className={s.meta}>Nichts erledigt.</p>
              ) : (
                <AufgabenListe liste={erledigtImSprint} unterVon={unterVon} label={`Erledigt in ${sp.name}`} nurLesen inKarte {...gemeinsam} />
              )
            ) : null}
          </Karte>
        );
      })}
    </>
  );
}
